"use client";

import { AnimatePresence, motion } from "motion/react";
import { Check, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "../lib/utils";

export interface LanguageOption {
  code: string;
  label: string;
  flag?: string;
}

interface LanguageSelectorProperties {
  languages: LanguageOption[];
  selectedLanguage: string;
  onLanguageChange: (code: string) => void;
  className?: string;
}

export function LanguageSelector({
  languages,
  selectedLanguage,
  onLanguageChange,
  className,
}: LanguageSelectorProperties) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownReference = useRef<HTMLDivElement>(null);
  const triggerReference = useRef<HTMLButtonElement>(null);
  const optionReferences = useRef<(HTMLButtonElement | null)[]>([]);
  const listboxId = useId();

  const selected =
    languages.find((lang) => lang.code === selectedLanguage) || languages[0];
  const selectedIndex = languages.findIndex(
    (lang) => lang.code === selectedLanguage,
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownReference.current &&
        !dropdownReference.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!open) {
        // Open dropdown on arrow down or enter
        if (
          event.key === "ArrowDown" ||
          event.key === "Enter" ||
          event.key === " "
        ) {
          event.preventDefault();
          setOpen(true);
          setActiveIndex(Math.max(selectedIndex, 0));
        }
        return;
      }

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          setActiveIndex((previous) => {
            const next = previous < languages.length - 1 ? previous + 1 : 0;
            optionReferences.current[next]?.focus();
            return next;
          });
          break;
        }

        case "ArrowUp": {
          event.preventDefault();
          setActiveIndex((previous) => {
            const next = previous > 0 ? previous - 1 : languages.length - 1;
            optionReferences.current[next]?.focus();
            return next;
          });
          break;
        }

        case "Home": {
          event.preventDefault();
          setActiveIndex(0);
          optionReferences.current[0]?.focus();
          break;
        }

        case "End": {
          event.preventDefault();
          setActiveIndex(languages.length - 1);
          optionReferences.current[languages.length - 1]?.focus();
          break;
        }

        case "Enter":
        case " ": {
          event.preventDefault();
          if (activeIndex >= 0) {
            onLanguageChange(languages[activeIndex].code);
            setOpen(false);
            triggerReference.current?.focus();
          }
          break;
        }

        case "Escape": {
          event.preventDefault();
          setOpen(false);
          triggerReference.current?.focus();
          break;
        }

        case "Tab": {
          setOpen(false);
          break;
        }
      }
    },
    [open, activeIndex, languages, selectedIndex, onLanguageChange],
  );

  // Focus first option when dropdown opens
  useEffect(() => {
    if (open) {
      const indexToFocus = Math.max(selectedIndex, 0);
      setActiveIndex(indexToFocus);
      setTimeout(() => optionReferences.current[indexToFocus]?.focus(), 0);
    }
  }, [open, selectedIndex]);

  return (
    <div
      className={cn("relative inline-block", className)}
      ref={dropdownReference}
    >
      {/* Trigger Button */}
      <motion.button
        ref={triggerReference}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          "flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-xs font-semibold",
          "bg-white/90 backdrop-blur-sm shadow-sm",
          "border-gray-200",
          "text-gray-800",
          "hover:bg-white hover:border-gray-300 transition-all",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer",
        )}
        aria-label="Selecteer taal"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
      >
        {selected.flag && (
          <span className="text-sm" aria-hidden="true">
            {selected.flag}
          </span>
        )}
        <span>{selected.label}</span>
        <ChevronDown
          className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            id={listboxId}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute left-0 mt-2 w-48 rounded-xl overflow-hidden z-50",
              "bg-white/95 backdrop-blur-xl",
              "shadow-lg border-2 border-gray-200",
            )}
            role="listbox"
            aria-label="Beschikbare talen"
            aria-activedescendant={
              activeIndex >= 0
                ? `language-option-${languages[activeIndex].code}`
                : undefined
            }
          >
            {languages.map((lang, index) => {
              const isSelected = selectedLanguage === lang.code;
              const isActive = activeIndex === index;
              return (
                <motion.button
                  key={lang.code}
                  id={`language-option-${lang.code}`}
                  ref={(element) => {
                    optionReferences.current[index] = element;
                  }}
                  onClick={() => {
                    onLanguageChange(lang.code);
                    setOpen(false);
                    triggerReference.current?.focus();
                  }}
                  onKeyDown={handleKeyDown}
                  onMouseEnter={() => setActiveIndex(index)}
                  whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                  className={cn(
                    "flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors cursor-pointer",
                    "focus:outline-none focus:bg-blue-100",
                    isSelected
                      ? "font-semibold text-blue-600 bg-blue-50"
                      : "text-gray-800 hover:bg-gray-100",
                    isActive && !isSelected && "bg-gray-100",
                  )}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={isActive ? 0 : -1}
                >
                  {lang.flag && (
                    <span className="text-base" aria-hidden="true">
                      {lang.flag}
                    </span>
                  )}
                  <span className="flex-1">{lang.label}</span>
                  {isSelected && (
                    <Check
                      className="h-4 w-4 text-blue-600"
                      aria-hidden="true"
                    />
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
